import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { Box } from '@opendoor/atoms/box';
import { Loader } from '@opendoor/atoms/loader';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';

import { useAnalyticsPrefix, useAuthActions } from '../../AuthContext';
import {
  AsyncFeatureFlagGate,
  type DefaultAsyncFeatureFlagProps,
} from '../../flags/FeatureFlagGate';
import { REGISTER_LITE_FLAG } from '../../flags/flagKeys';
import { sanitizeRedirectPath } from '../../oauth/redirectPath';
import {
  type AccountFormSuccessResult,
  RESET_PASSWORD_STEP,
  useAccountForm,
} from '../AccountFormContext';
import { AccountFormProvider, type AccountFormProviderProps } from '../AccountFormProvider';
import { type ExperimentExposure, reportExperimentExposure } from '../experimentExposure';
import { EntryMethodGroups } from './EntryMethodGroups';
import { PASSWORD_SETUP_STEP } from './humanStatus';
import { MagicLinkModule } from './MagicLinkModule';
import { LOGIN_LINK_STEP, PasswordModule } from './PasswordModule';
import { AuthFormError } from './primitives/AuthFormError';
import { ConsentDisclaimer } from './primitives/ConsentDisclaimer';
import {
  isRegistrationEmailConflict,
  routeRegistrationEmailConflict,
} from './registrationConflict';
import { registrationFailureMessages } from './registrationFailureMessages';
import {
  RegistrationModule,
  type RegistrationResult,
  type RegistrationSubmission,
  RegistrationSubmissionError,
  type RegistrationSuccessContext,
} from './RegistrationModule';
import { ResetPasswordRequestModule } from './ResetPasswordRequestModule';

export interface AccountFormV2Props extends Omit<AccountFormProviderProps, 'children'> {
  className?: string;
  /**
   * Reports the arm this render belongs to, once, when the form paints.
   *
   * Set by whatever bucketed the seller — `AccountForm`'s gate does it — and
   * left unset by a surface composing this form outside an experiment, so an
   * unbucketed mount cannot add rows to an arm it was never in.
   */
  experimentExposure?: ExperimentExposure;
  /**
   * Entry-screen heading. A thin naming slot, per the `forms.ts` boundary —
   * omitted renders the default; `null` or `''` renders none.
   */
  heading?: React.ReactNode;
  /** Registration-step heading. Separate slot because legacy labels the two screens independently. */
  registrationHeading?: React.ReactNode;
  /**
   * Runs the registration step's experiment arm: no password field, account
   * finished through an emailed set-up link. The default collects a password,
   * matching cosmos production `/login`, so a surface that never wires
   * `REGISTER_LITE_FLAG` renders parity behavior (GROWTH-3103).
   *
   * Pass this to pin an arm outright — reception-fe does, because its funnel has
   * never collected a password, so lite is its control rather than its
   * experiment. Leave it off and pass {@link registerLiteFlagProps} to let the
   * flag choose. A pinned value wins: an explicit arm is a surface's own
   * contract, not something an experiment should override.
   */
  passwordlessRegistration?: boolean;
  /**
   * Evaluator for {@link REGISTER_LITE_FLAG}, which chooses the registration
   * arm when `passwordlessRegistration` is left unset. Same injected-evaluator
   * shape as `AccountForm`'s ramp gate — auth-fe never reads a flag source
   * itself — and the flag key defaults to `REGISTER_LITE_FLAG`.
   *
   * The gate holds the registration step behind a loader until the flag
   * resolves, so the arm is decided before the step paints. That is deliberate:
   * an unresolved read is `false`, so rendering through it would show the
   * password field, let a password manager fill it, then unmount the field on
   * the flip to lite.
   */
  registerLiteFlagProps?: DefaultAsyncFeatureFlagProps;
  /**
   * Sends the sign-in magic link when the password step's "Email a login link"
   * is chosen. The consumer's handler is the only path to the passwordless
   * mutation — it mints a real sign-in token and carries the consumer's
   * `redirectTo` — neither of which the session write-path's
   * `requestPasswordReset` can do: for an account with a real password that
   * endpoint mails Devise's change-your-password email, not a login link.
   * Resolve `success: false` (or throw) when no email went out. Left unset,
   * the step falls back to `requestPasswordReset`, so a surface without a
   * passwordless client keeps its current behavior.
   */
  onPasswordlessLoginRequest?: (
    email: string,
  ) => Promise<{ success: boolean; errors?: Array<string> }>;
  /**
   * Observability side channel fired once when the registration step's write
   * succeeds, before completion navigates. Registration only — logins never
   * fire it — so a consumer can attach account-creation tracking without
   * auth-fe knowing its schema. See
   * {@link RegistrationModuleProps.onRegistrationSuccess} for the throw
   * contract and the OAuth caveat (provider sign-ups complete through a
   * redirect flow and never reach it).
   */
  onRegistrationSuccess?: (context: RegistrationSuccessContext) => void;
}

const DEFAULT_ENTRY_HEADING = 'Sign in or create an account';

/**
 * COMPONENT segment for the entry screen's own controls. Matches `EmailModule`
 * and `SocialAuthModule`, which render on the same surface.
 */
const ENTRY_ANALYTICS_COMPONENT = 'account-form';

/**
 * Collapses the values that should render no heading to `null`, so both naming
 * slots behave the same. `''` is legacy's suppression signal, and `false` is what
 * the idiomatic `heading={isReturning && 'Welcome back'}` yields — either would
 * otherwise leave a nameless `h1` that is still announced as a heading.
 */
function normalizeHeading(heading: React.ReactNode): React.ReactNode {
  return heading === '' || heading === false ? null : heading;
}

/**
 * Post-registration confirmation step: the set-up link is already in the new
 * user's inbox, so the magic-link module mounts in its `set-up` variant (no
 * fresh send, cooldown only).
 */
const SETUP_LINK_STEP = 'magic-link-setup';

/**
 * Upper bound on how long registration completion waits for the best-effort
 * marketing-consent write before advancing. The write keeps running past this;
 * the bound only stops a hung consent request from stranding the user on the
 * submit spinner for an account that already exists.
 */
const CONSENT_WRITE_TIMEOUT_MS = 5000;

/**
 * The default step graph: reads the shared `step` and renders the module that
 * owns that screen. The routing table is the shell's — the sequence's
 * invariants (human-status routing, the register write-path and which
 * credential it sends) stay in auth-fe rather than being handed to consumers,
 * who compose à la carte under a bare `AccountFormProvider` when they want a
 * different graph.
 */
type AccountFormV2StepsProps = Pick<
  AccountFormV2Props,
  | 'heading'
  | 'registrationHeading'
  | 'passwordlessRegistration'
  | 'registerLiteFlagProps'
  | 'onPasswordlessLoginRequest'
  | 'onRegistrationSuccess'
>;

const AccountFormV2Steps: React.FC<AccountFormV2StepsProps> = ({
  heading: rawHeading = DEFAULT_ENTRY_HEADING,
  registrationHeading,
  passwordlessRegistration,
  registerLiteFlagProps,
  onPasswordlessLoginRequest,
  onRegistrationSuccess,
}) => {
  const heading = normalizeHeading(rawHeading);
  const { step, setStep, email, formError } = useAccountForm();
  const { register, requestHumanStatus, recordMarketingSmsConsent } = useAuthActions();
  const analyticsPrefix = useAnalyticsPrefix();

  // RegistrationModule takes an injectable `register` so the shell can bind it
  // to the real write-path. The module treats a throw as the failure signal, so
  // map a failed AuthResult to a thrown error here.
  //
  // The credential comes from whether the module collected a password, not from
  // a second read of the flag: the field that rendered is the authority on what
  // the user supplied, so the two can't disagree.
  const registerAccount = useCallback(
    async (submission: RegistrationSubmission): Promise<RegistrationResult> => {
      const result = await register({
        email: submission.email,
        fullName: submission.fullName,
        primaryPhone: submission.phoneNumber,
        // Falsy, not nullish: `password?: string` admits `''`, and an empty
        // string is not a credential the user chose — it must route to the
        // server-generated one rather than POST `{ password: '' }`.
        ...(submission.password
          ? { password: submission.password }
          : { hasGeneratedPassword: true as const }),
      });
      if (!result.success) {
        // This screen's email field is editable, so the address submitted here
        // may be one the entry step never looked up. Rails rejecting it as taken
        // is the signal to re-run that lookup and route to sign-in, rather than
        // leaving "Email has already been taken" on a screen with no path to a
        // password or a magic link. `submission.email`, not the context `email`:
        // this callback's closure holds the pre-edit address, which is exactly
        // the one this is not about.
        if (isRegistrationEmailConflict(result.error)) {
          // Throws once it has routed, so the consent write and `onSuccess` below
          // cannot run for an account this flow did not create. It returns only
          // when there was nowhere to route — the re-check says the address is
          // still new, so the rejection was some other email rule — and then the
          // throw below renders it.
          await routeRegistrationEmailConflict({
            email: submission.email,
            requestHumanStatus,
            setStep,
          });
        }
        throw new RegistrationSubmissionError(registrationFailureMessages(result.error), {
          cause: result.error,
        });
      }
      // Marketing-SMS consent is its own post-registration write (a durable
      // Consent record on the customer service), not part of `/register`.
      // RegistrationModule always shows the consent checkboxes, so
      // `marketingOptIn` is a value the user actually saw and set — record it,
      // never synthesize one — and `consentLanguage` comes from the spec that
      // rendered, so the recorded version names the copy shown. The registered
      // email goes along so the write can't land on a different customer. The
      // write is awaited (a consumer's `onSuccess` may navigate and unload the
      // page before it lands) but time-boxed: a hung consent request must not
      // strand the user on the spinner. Failures are best-effort — the provider
      // logs them via `trackError` — so neither a rejection nor the timeout
      // blocks a completed registration.
      const consentWrite = recordMarketingSmsConsent({
        optIn: submission.marketingOptIn,
        email: submission.email,
        consentLanguage: submission.consentLanguage,
      }).then(
        () => undefined,
        () => undefined,
      );
      let consentTimeout: ReturnType<typeof setTimeout> | undefined;
      const consentDeadline = new Promise<void>((resolve) => {
        consentTimeout = setTimeout(resolve, CONSENT_WRITE_TIMEOUT_MS);
      });
      await Promise.race([consentWrite, consentDeadline]);
      clearTimeout(consentTimeout);
      // Only the passwordless arm needs the set-up confirmation: it is where the
      // user learns an email is coming. With a password the account is already
      // usable, so completing through `onSuccess`/the redirect signs them in
      // rather than parking them on a screen about an email they won't get — and
      // the default completion always navigates, falling back to the site root
      // when `RegistrationResult.redirectTo` is absent (it is documented
      // optional, though Rails resolves one through `PostLoginRouter` today).
      if (!submission.password) {
        setStep(SETUP_LINK_STEP);
      }
      return { redirectTo: result.data.redirectTo };
    },
    [register, requestHumanStatus, recordMarketingSmsConsent, setStep],
  );

  // Bound here rather than inside MagicLinkModule so the module keeps its
  // address-free `onResend` contract: the consumer's handler wants the email,
  // and this component is the one holding it.
  const sendLoginLink = useMemo(() => {
    if (onPasswordlessLoginRequest === undefined) {
      return undefined;
    }
    return async () => (await onPasswordlessLoginRequest(email)).success;
  }, [onPasswordlessLoginRequest, email]);

  switch (step) {
    case 'password':
      return <PasswordModule />;
    case PASSWORD_SETUP_STEP:
      return <MagicLinkModule />;
    case LOGIN_LINK_STEP:
      return <MagicLinkModule onResend={sendLoginLink} />;
    case SETUP_LINK_STEP:
      return <MagicLinkModule variant="set-up" />;
    case 'new': {
      const registration = (passwordless: boolean) => (
        <RegistrationModule
          register={registerAccount}
          onRegistrationSuccess={onRegistrationSuccess}
          heading={normalizeHeading(registrationHeading)}
          passwordless={passwordless}
        />
      );
      // A pinned arm skips the gate entirely, so a surface with its own contract
      // never pays for a flag read it doesn't consult. Neither pinned nor wired
      // is the control, which is what makes an unwired surface render parity.
      if (passwordlessRegistration !== undefined || registerLiteFlagProps === undefined) {
        return registration(passwordlessRegistration ?? false);
      }
      const { flag = REGISTER_LITE_FLAG, ...flagProps } = registerLiteFlagProps;
      return (
        <AsyncFeatureFlagGate
          flag={flag}
          enabled={registration(true)}
          disabled={registration(false)}
          // Explicit, not the `disabled` default: that default would paint the
          // control's password field while the flag is still in flight.
          loading={
            <Box className="flex w-full items-center justify-center">
              <Loader size="2xl" />
            </Box>
          }
          {...flagProps}
        />
      );
    }
    case RESET_PASSWORD_STEP:
      // `email || undefined`, not `email`: only nullish leaves the module free to
      // fall back to its own `?email=` read.
      return <ResetPasswordRequestModule defaultEmail={email || undefined} />;
    default:
      // ENTRY_STEP and any unrecognized step render the entry screen. The heading
      // lives here, not in the layout wrapper, because every other step renders
      // its own `h1`.
      return (
        <>
          {heading != null ? (
            <Text as="h1" className="text-subheader-md text-od-content-primary">
              {heading}
            </Text>
          ) : null}
          <ConsentDisclaimer analyticsPrefix={`${analyticsPrefix}-${ENTRY_ANALYTICS_COMPONENT}`} />
          {/* One alert region for the screen, above every method — where the
              Figma puts it, and the only spot that reads the same whether the
              failure came from the email lookup or a method button below it. */}
          <AuthFormError serverErrors={formError} />
          <EntryMethodGroups />
        </>
      );
  }
};

/**
 * Batteries-included convenience form: `AccountFormProvider` plus the default
 * module set, routed by the shared step. The entry step is the social-auth
 * stack, an "Or" divider, then the email field; submitting the email routes to
 * the password, password-setup, or new-user screen the human-status lookup implies,
 * so this wrapper completes a real login or registration, not just the entry
 * step. Completion included: with no consumer `onSuccess`, a finished flow
 * navigates to `redirectTo` (falling back to the server's post-login route),
 * so mounting this bare still logs the user in visibly. Modules reach the
 * write-path through `useAuthActions()`, supplied by an `AuthActionsProvider`
 * or `AuthBaseUrlContextProvider`. For anything else — a different module set, a
 * different step graph — compose `AccountFormProvider` directly instead of
 * reaching for a `children` escape hatch here.
 */
export const AccountFormV2: React.FC<AccountFormV2Props> = ({
  className,
  experimentExposure,
  heading,
  registrationHeading,
  passwordlessRegistration,
  registerLiteFlagProps,
  onPasswordlessLoginRequest,
  onRegistrationSuccess,
  ...providerProps
}) => {
  const { onSuccess, redirectTo } = providerProps;
  // Sanitized here, at the boundary where the value enters the shell, rather than
  // inside the navigation callback: `redirectTo` arrives from a query parameter
  // (cosmos reads `?redirectTo=` off the URL), and a consumer that supplies its
  // own `onSuccess` replaces the callback entirely — so gating there would hand
  // the raw value to exactly the consumers most likely to navigate with it.
  const safeRedirectTo = sanitizeRedirectPath(redirectTo);
  // Once per mount. The gate gives each arm its own key, so switching arms
  // remounts and reports the arm actually shown rather than reusing the first.
  const reported = useRef(false);
  useEffect(() => {
    if (experimentExposure === undefined || reported.current) {
      return;
    }
    reported.current = true;
    reportExperimentExposure(experimentExposure);
  }, [experimentExposure]);
  // The consumer's `redirectTo` wins over the server's post-login route: it is
  // the destination the social buttons already honor, so the landing page must
  // not differ by auth method on the same form.
  //
  // Falling back to the site root rather than no-oping: a completed auth has to
  // land somewhere, and staying put leaves a signed-in user looking at the form
  // they just submitted. The path is relative on purpose, so it resolves to
  // whatever host is serving the form (opendoor.com, a demo environment) without
  // this package knowing the environment.
  const navigateOnSuccess = useCallback(
    ({ redirectTo: serverRedirectTo }: AccountFormSuccessResult) => {
      window.location.assign(safeRedirectTo ?? sanitizeRedirectPath(serverRedirectTo) ?? '/');
    },
    [safeRedirectTo],
  );

  return (
    <AccountFormProvider
      {...providerProps}
      redirectTo={safeRedirectTo}
      onSuccess={onSuccess ?? navigateOnSuccess}
    >
      <Box className={cn('flex w-full max-w-[25rem] flex-col gap-4', className)}>
        <AccountFormV2Steps
          heading={heading}
          registrationHeading={registrationHeading}
          passwordlessRegistration={passwordlessRegistration}
          registerLiteFlagProps={registerLiteFlagProps}
          onPasswordlessLoginRequest={onPasswordlessLoginRequest}
          onRegistrationSuccess={onRegistrationSuccess}
        />
      </Box>
    </AccountFormProvider>
  );
};
